import { ConfigFilterItem } from 'Core/interfaces'

import { MONTHES_SHORTLY, QUARTERS_SHORTLY } from '../constants'

import { Location } from './Location'

export enum KpiFilterFields {
	serviceSiteId = 'serviceSiteId',
	categoryCode = 'categoryCode'
}

export enum KpiMetricFilterFields {
	kpiId = 'kpiId',
	kpiCategoryMetric = 'kpiCategoryMetric',
	year = 'year'
}

export enum KpiFields {
	id = 'id',
	year = 'year',
	quantity = 'quantity',
	uom = 'uom',
	list = 'list',
	quantitySelect = 'quantitySelect',
	name = 'name',
	termCodeType = 'termCodeType',
	measureType = 'measureType',
	kpiValueOptions = 'kpiValueOptions',
	kpiUOMs = 'kpiUOMs',
	kpiFieldId = 'kpiFieldId',
	description = 'description',
	rowKey = 'rowKey',
	companyserviceSiteId = 'companyserviceSiteId',
	uomCode = 'uomCode'
}

export enum TermCodeType {
	MONTHLY = 'MONTHLY',
	QUARTERLY = 'QUARTERLY',
	YEARLY = 'YEARLY'
}

export enum MeasureType {
	number = 'Number',
	boolean = 'Boolean',
	string = 'String',
	predefinedString = 'PredefinedString'
}

export declare namespace Kpi {
	interface Basic {
		rowKey: number
		companyserviceSiteId?: number
		id: number
		name: string
		description: string
		uomCode: string
		termCodeType: TermCodeType
		measureType: MeasureType
		kpiUOMs: CodeEntity[]
		kpiValueOptions: Entity[]
		year: ExpandedLabelValue | null
		quantity: Key | null | LabelValueExtra
		uom?: LabelValueExtra | null
	}

	interface MeasureRequest {
		kpiId: number
		serviceSiteId?: number
		year?: Key
		measureTermCode?: string
		includeChange?: boolean
	}

	interface MeasurePayload extends MeasureRequest {
		rowKey: number
	}

	interface MeasureResponse {
		measureId: number
		measureValue: string
	}

	interface SavedMeasureData {
		rowKey: number
		measureId: number
	}

	interface PreviousPeriodValue {
		value: number
		uom: string
		percentChange: number
		isExceed: boolean
	}

	interface KpiLocationMeasure {
		id: number
		kpiId: number
		serviceSiteId: number
		term: string
		uomCode: string
		value: string
		year: number
		createdBy: BasicString
		createDate: BasicString
		previousPeriodValue: PreviousPeriodValue | null
	}

	interface KpiLocationMeasureForm extends KpiLocationMeasure {
		rowKey: number
	}

	interface EditTableConfig<SelectFieldNames extends string> {
		selects: Record<
			SelectFieldNames,
			{
				options: LabelValueExtra<Key>[]
			}
		>
	}

	type FieldType = Record<KpiFields, Key | boolean | TermCodeType | Entity[] | CodeEntity[]>

	namespace Form {
		interface KpiList extends Basic {}
		interface Edit {
			// do not change, need for react hook form useFieldArray
			list: {
				rowKey: number
				companyserviceSiteId?: number
				id: number
				name: string
				description: string
				uomCode: string
				termCodeType: TermCodeType
				measureType: MeasureType
				kpiUOMs: CodeEntity[]
				kpiValueOptions: Entity[]
				year: ExpandedLabelValue | null
				quantity: Key | null | LabelValueExtra
				uom?: LabelValueExtra | null
			}[]
		}
	}

	namespace Filter {
		interface Config {
			[KpiFilterFields.serviceSiteId]: ConfigFilterItem<string, null>
			[KpiFilterFields.categoryCode]: ConfigFilterItem<string, null>
		}
	}

	namespace Request {
		interface Add {
			kpiId: Key
			serviceSiteId: Key
			year?: Key
			measureTermCode?: Key
			measureValue?: Key | boolean
			uomCode?: Key
			rowKey: number
		}
		interface Edit {
			measureValue: string
			uomCode?: string
		}
	}
	interface DirtyConfig {
		rowKey: number
		isDirty: boolean
	}
	namespace Metric {
		interface ResponseBasic extends Entity {
			companyserviceSiteId: number
		}
		interface Basic extends Kpi.Basic {
			locId: number
		}
		interface FilterOptions {
			category: CodeEntity
			kpis: Kpi.Basic[]
		}

		interface FormattedFilterOptions extends LabelValue<number> {
			kpiOptions: LabelValue[]
		}

		namespace Filter {
			interface Config {
				[KpiMetricFilterFields.kpiCategoryMetric]: ConfigFilterItem<string, null>
				[KpiMetricFilterFields.kpiId]: ConfigFilterItem<string, null>
			}
			interface EditConfig extends Config {
				[KpiMetricFilterFields.year]: ConfigFilterItem<string, null>
			}
		}
	}

	interface OptionsByserviceSiteId {
		category: IdCodeName
		kpis: (Entity & {
			description: string
			uomCode: string
			termCodeType: string
			measureType: string
			kpiUOMs: IdCodeName[]
			kpiValueOptions: Entity[]
		})[]
	}

	export type TermRow = Kpi.KpiLocationMeasure & {
		[key in MONTHES_SHORTLY | QUARTERS_SHORTLY]?: Kpi.KpiLocationMeasure
	}

	export type MetricTermRow = Location.CompanyLocation & {
		[key in MONTHES_SHORTLY | QUARTERS_SHORTLY]?: Kpi.KpiLocationMeasure
	} & Kpi.KpiLocationMeasure
}
